import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useLingui } from "@lingui/react/macro";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { DataTable } from "@/components/data-table";
import { CostValue } from "@/components/recipes/cost/CostValue";
import type { CostedLine } from "@/lib/api/requests/recipes";
import {
  formatGrams,
  formatMillilitres,
  kitchenAmount,
  type BatchScale,
} from "@/lib/recipes/staged-method";

const CUP_ML = 236.5882365;

interface RecipeIngredientMeasuresProps {
  lines: CostedLine[] | undefined;
  scale: BatchScale;
  defaultOpen: boolean;
}

/**
 * The expanded ingredient table under a recipe card: every line by weight and
 * by volume, the density that bridges the two, the yield after trim and peel,
 * how much to take from the case to end up with it, and what it costs — all
 * for the batch being made. The figures come from each ingredient's supplier
 * item (the pack-size converter) through the cost summary; a figure the item
 * can't support is a dash, never a guess.
 */
export function RecipeIngredientMeasures({ lines, scale, defaultOpen }: RecipeIngredientMeasuresProps) {
  const { t } = useLingui();
  const [open, setOpen] = useState(defaultOpen);

  const columns = useMemo<ColumnDef<CostedLine, unknown>[]>(() => {
    const dash = <span className="text-(--color-text-secondary)">—</span>;
    const cell = "h-auto px-3 py-2.5 text-sm text-(--color-text-primary) whitespace-nowrap";
    return [
      {
        id: "name",
        header: t`Ingredient`,
        cell: ({ row }) => <span className="font-semibold">{row.original.name}</span>,
        meta: { className: cell },
      },
      {
        id: "amount",
        header: t`Recipe amount`,
        cell: ({ row }) => kitchenAmount(row.original.quantity * scale, row.original.unit),
        meta: { className: cell },
      },
      {
        id: "weight",
        header: t`Weight`,
        cell: ({ row }) => {
          const grams = row.original.measures?.grams;
          return grams != null ? formatGrams(grams * scale) : dash;
        },
        meta: { className: `${cell} text-right` },
      },
      {
        id: "volume",
        header: t`Volume`,
        cell: ({ row }) => {
          const ml = row.original.measures?.millilitres;
          return ml != null ? formatMillilitres(ml * scale) : dash;
        },
        meta: { className: `${cell} text-right` },
      },
      {
        id: "density",
        header: t`Density`,
        cell: ({ row }) => {
          const density = row.original.measures?.densityGPerMl;
          if (density == null) return dash;
          // Said the way the converter asks for it: what a level cup weighs.
          const perCup = formatGrams(density * CUP_ML);
          return (
            <span title={t`${density} g per mL`}>{t`1 cup = ${perCup}`}</span>
          );
        },
        meta: { className: cell },
      },
      {
        id: "yield",
        header: t`Yield`,
        cell: ({ row }) => {
          const pct = row.original.measures?.yieldPct;
          return pct != null ? `${Number(pct.toFixed(1))}%` : dash;
        },
        meta: { className: `${cell} text-right` },
      },
      {
        id: "asPurchased",
        header: t`As purchased`,
        cell: ({ row }) => {
          const measures = row.original.measures;
          if (measures?.asPurchasedQuantity == null) return dash;
          const asPurchased = measures.asPurchasedQuantity * scale;
          const grams =
            measures.grams != null && measures.yieldPct ? formatGrams((measures.grams * scale * 100) / measures.yieldPct) : null;
          return (
            <span>
              {kitchenAmount(asPurchased, row.original.unit)}
              {grams && measures.family !== "weight" && (
                <span className="text-(--color-text-secondary)"> ({grams})</span>
              )}
            </span>
          );
        },
        meta: { className: cell },
      },
      {
        id: "supplier",
        header: t`From`,
        cell: ({ row }) => row.original.measures?.supplierName ?? dash,
        meta: { className: `${cell} hidden lg:table-cell` },
      },
      {
        id: "cost",
        header: t`Line cost`,
        cell: ({ row }) => (
          <CostValue
            value={row.original.extendedCost == null ? null : row.original.extendedCost * scale}
            reason={row.original.unavailableReason}
            detail={row.original.unavailableDetail}
          />
        ),
        meta: { className: `${cell} text-right` },
      },
    ];
  }, [t, scale]);

  return (
    <section aria-label={t`Ingredient conversions`} className="flex flex-col gap-3 print:hidden">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-fit items-center gap-2 text-sm font-semibold text-(--color-text-primary)"
      >
        <ChevronDown className={cn("size-4 transition-transform", !open && "-rotate-90")} />
        {t`Ingredient conversions`}
        <span className="font-normal text-(--color-text-secondary)">{t`Weight, volume, density and yield`}</span>
      </button>
      {open && (
        <div className="overflow-x-auto rounded-2xl border border-(--color-border)">
          <DataTable
            columns={columns}
            data={lines ?? []}
            emptyMessage={lines ? t`This recipe has no ingredients yet.` : t`Loading conversions…`}
          />
        </div>
      )}
    </section>
  );
}
